import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import {
  type Traveler,
  TravelerRole,
} from "./types";
import { travelerFromApi } from "./travelerMap";
import { productInfoMap, travelInsurancePlans } from "./constants";
import {
  getTourOrderDetail,
  type PreCreateOrderData,
} from "@/api/tourOrder";
import {
  confirmGroupTourOrder,
  type PreCreateOrderData as GroupPreCreateData,
} from "../route-detail-product/api/order";
import { toastWarning } from "@/utils/toast";
import { fetchUserTravelerList } from "@/api/user";


import { OrderHeader } from "./components/OrderHeader";
import { TripSummary } from "./components/TripSummary";
import { ProductCard } from "./components/ProductCard";
import { ContactInfo } from "./components/ContactInfo";
import { TravelersList } from "./components/TravelersList";
import { SpecialRequest } from "./components/SpecialRequest";
import { OrderFooter } from "./components/OrderFooter";
import { DateSelector } from "./components/DateSelector";
import { DatePickerModal } from "./components/DatePickerModal";

/** 联系人信息表单 */
interface ContactForm {
  name: string;
  phone: string;
  email: string;
}

/**
 * 旅游路线订单数据
 */
interface TourOrderDetail {
  order_no: string;
  total_amount: number;
  adult_price: number; // 成人单价
  child_price: number; // 儿童单价
  pickupLocation?: string;
  returnLocation?: string;
  pickupDate?: string;
  rentDays?: number;
  carTitle?: string;
  coverImage?: string;
  carAmount?: string;
  special_requests?: string[];
  contact?: { name: string; phone: string; email?: string };
}

export default function OrderFormPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const routeState = location.state as {
    orderId?: number;
    orderNo?: string;
    routeId?: number;
    routeTitle?: string;
    unitPrice?: number;
    guests?: number;
    totalAmount?: number;
    preCreatedData?: PreCreateOrderData | GroupPreCreateData;
    bookingFormData?: {
      departureDate?: string;
      endDate?: string;
      priceId?: number;
      roomType?: string;
      salePrice?: string;
      singleRoomSupplement?: string;
      singleBikeSeat?: string;
      singleRoomCount?: number;
      singleBikeCount?: number;
      peopleCount?: number;
      contactName?: string;
      contactPhone?: string;
      remark?: string;
    };
  };

  const orderId = routeState?.orderId;

  // 判断是否为团游新格式（从线路详情下单）
  const preCreatedData = routeState?.preCreatedData as GroupPreCreateData | undefined;
  const isGroupFormat = preCreatedData && "periods" in (preCreatedData as object);

  // 联系人信息
  const [contact, setContact] = useState<ContactForm>({
    name: "",
    phone: "",
    email: "",
  });

  // 订单详情
  const [orderDetail, setOrderDetail] = useState<TourOrderDetail | null>(null);
  const [loading, setLoading] = useState(false);

  // 出行人
  const [travelers, setTravelers] = useState<Traveler[]>([]);
  const travelersCount = travelers.length;

  // 保险
  const [insurance, setInsurance] = useState<
    "none" | "basic" | "standard" | "premium"
  >("none");

  // 特殊需求
  const [specialRequest, setSpecialRequest] = useState("");

  // 出行日期选择器
  const [showDatePicker, setShowDatePicker] = useState(false);

  // 线路预创建产品数据
  const [tourProduct, setTourProduct] = useState<{
    days: number;
    nights: number;
    calendarList: import("@/api/tourOrder").CalendarDateItem[];
  } | null>(null);

  // 有 orderId 时获取订单详情
  useEffect(() => {
    if (!orderId) return;
    setLoading(true);
    getTourOrderDetail(orderId)
      .then((res) => {
        if (res.data.code === 200 && res.data.data) {
          const d = res.data.data;
          setOrderDetail({
            order_no: d.order_no,
            total_amount: Number(d.total_amount),
            adult_price: Number(d.price?.adult?.amount ?? 0),
            child_price: Number(d.price?.child?.amount ?? 0),
            pickupLocation: d.departure_city,
            returnLocation: d.destinations,
            pickupDate: d.departure_date,
            rentDays: d.days,
            carTitle: d.product?.title ?? "",
            coverImage: d.product?.cover_image,
            carAmount: String(d.total_amount),
            special_requests: d.special_requests,
          });
          if (d.contact) {
            setContact({
              name: d.contact.name || "",
              phone: d.contact.phone || "",
              email: d.contact.email || "",
            });
          }
          if (d.travelers?.length) {
            setTravelers(d.travelers.map(travelerFromApi));
          }
        }
      })
      .catch((err) => console.error("获取线路订单详情失败:", err))
      .finally(() => setLoading(false));
  }, [orderId]);

  // 无 orderId 时预创建产品数据
  useEffect(() => {
    if (orderId) return;
    const pd = routeState?.preCreatedData as unknown as Record<string, unknown> | undefined;
    const isGroupFormat = pd && "periods" in (pd as object);
    const product = pd?.product as Record<string, unknown> | undefined;

    // 从预创建数据中提取初始产品信息（两种格式通用字段）
    const initProduct: Partial<TourOrderDetail> = {
      pickupLocation: (product?.departure_city as string) || "",
      returnLocation: (product?.destinations as string) || "",
      rentDays: (product?.days as number) || 0,
      carTitle: (product?.title as string) || routeState?.routeTitle || "",
      coverImage: (product?.cover_image as string) || "",
    };

    // 新格式：从 bookingFormData 提取日期和联系人
    if (isGroupFormat && routeState?.bookingFormData) {
      const bf = routeState.bookingFormData;
      initProduct.pickupDate = bf.departureDate || "";
      if (bf.contactName || bf.contactPhone) {
        setContact({
          name: bf.contactName || "",
          phone: bf.contactPhone || "",
          email: "",
        });
      }
      // 设置初始价格
      const salePrice = bf.salePrice ? Number(bf.salePrice) : 0;
      initProduct.adult_price = salePrice;
      initProduct.total_amount = salePrice * (bf.peopleCount || 1);
      initProduct.carAmount = String(salePrice);
    }

    // 老格式：从 tourOrder 返回结构提取
    if (pd && !isGroupFormat) {
      const oldPd = pd as unknown as PreCreateOrderData;
      initProduct.total_amount = oldPd.product.default_price.adult_price;
      initProduct.adult_price = oldPd.product.default_price.adult_price;
      initProduct.child_price = oldPd.product.default_price.child_price;
      initProduct.pickupDate = oldPd.booking_config?.min_date || "";
      setTourProduct({
        days: oldPd.product.days,
        nights: oldPd.product.nights,
        calendarList: oldPd.calendar_list,
      });
    }

    // 新格式：直接拿 product 中的 days/nights（不再调旧 pre-create，避免 405）
    if (isGroupFormat && product) {
      setTourProduct({
        days: (product.days as number) || 1,
        nights: (product.nights as number) || 0,
        calendarList: [],
      });
    }

    setOrderDetail(initProduct as TourOrderDetail);
  }, [orderId, routeState]);

  // ── 进入页面即获取用户出行人库列表（回填常用出行人）────────────
  useEffect(() => {
    fetchUserTravelerList(1, 20)
      .then((list) => {
        if (list.length > 0 && travelers.length === 0) {
          const mapped = list.map((item) => ({
            name: item.name,
            type: TravelerRole.ADULT as any,
            idCard: item.card_no || "",
            phone: item.phone || undefined,
            idCardFront: item.id_card_front ?? undefined,
            idCardBack: item.id_card_back ?? undefined,
            travelerId: item.id,
            isDefault: item.is_default === 1,
          }));
          setTravelers(mapped);
        }
      })
      .catch((err) => console.error("获取出行人列表失败:", err));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // 产品信息
  const productInfo = productInfoMap.tour;

  // 金额
  const baseTotal = orderDetail?.total_amount ?? productInfo?.price ?? 0;
  // const insurancePrice =
  // travelInsurancePlans.find((p) => p.id === insurance)?.price ?? 0;
  // const insuranceCost = insurancePrice * travelersCount;

  // ★ 出行人各类型数量（用于页面总价展示）
  const adultCount = travelers.filter(
    (t) => t.type === TravelerRole.ADULT,
  ).length;
  const childCount = travelers.filter(
    (t) => t.type === TravelerRole.CHILD,
  ).length;

  // 总价 = 成人单价 × 成人数 + 儿童单价 × 儿童数（未添加出行人时默认按 1 人显示）
  // 兜底：若 adult_price 为 0（接口未配置），则取 child_price 或 baseTotal 作为展示基准
  const rawAdultPrice = orderDetail?.adult_price ?? 0;
  const childPrice = orderDetail?.child_price ?? 0;
  const adultPrice = rawAdultPrice || childPrice || baseTotal || productInfo?.price || 0;
  const effectiveAdultCount = adultCount || 1;
  const total = adultPrice * effectiveAdultCount + childPrice * childCount;

  // 确认订单 → 去支付
  const handleConfirmAndPay = async () => {
    if (loading) return;

    if (
      !contact.name.trim() ||
      !contact.phone.trim() ||
      !contact.email.trim()
    ) {
      toastWarning("请填写完整的联系人信息");
      return;
    }
    if (travelers.length === 0) {
      toastWarning("请添加出行人员");
      return;
    }

    // 校验必要参数
    if (!routeState?.routeId) {
      toastWarning("线路产品ID丢失，请返回重新选择");
      return;
    }
    if (!orderDetail?.pickupDate) {
      toastWarning("请选择出发日期");
      return;
    }

    // 房间数按人数简单计算；单房差/单车位优先从详情页携带数据
    const totalPeople = travelers.length;
    const roomCount = Math.max(1, Math.floor(totalPeople / 2));
    const singleRoomCount = routeState?.bookingFormData?.singleRoomCount ?? (totalPeople % 2);
    const singleBikeCount = routeState?.bookingFormData?.singleBikeCount ?? 0;

    setLoading(true);
    try {
      const res = await confirmGroupTourOrder({
        group_tour_id: routeState?.routeId ?? 0,
        start_date: orderDetail?.pickupDate || "",
        end_date: routeState?.bookingFormData?.endDate || "",
        room_type: routeState?.bookingFormData?.roomType || "",
        room_count: roomCount,
        single_room_count: singleRoomCount,
        single_bike_count: singleBikeCount,
        contact_name: contact.name,
        contact_phone: contact.phone,
        contact_email: contact.email || undefined,
        travelers: travelers.map((t) => ({
          name: t.name,
          type_name: t.type.split(" (")[0],
          id_card: t.idCard || undefined,
          phone: t.phone || undefined,
        })),
        special_requests: specialRequest ? [specialRequest] : undefined,
      });

      if (res.data.code !== 200) {
        toastWarning(res.data.message || "确认订单失败");
        return;
      }

      // 从确认订单接口返回值中取 order_no
      const data = res.data.data as Record<string, unknown> | undefined;
      const confirmedOrderNo = String(
        data?.order_no || data?.orderNo || data?.order_id || data?.orderId || ""
      );


      navigate("/payment", {
        state: {
          orderNo: confirmedOrderNo,
          totalAmount: String(total),
          type: "tour",
          ...(orderId != null ? { orderId } : {}),
        },
      });
    } catch (err) {
      console.error("确认订单失败:", err);
      toastWarning("网络错误，请稍后重试");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen pb-[calc(env(safe-area-inset-bottom,0px)+144px)]"
      style={{
        background: "#F7F7F7",
        fontFamily: "'SF Pro Text','PingFang SC','Inter',system-ui,sans-serif",
      }}
    >
      <OrderHeader />
      <div className="px-4 py-4 space-y-3">
        <TripSummary
          spec={productInfo?.spec || ""}
          pickupLocation={orderDetail?.pickupLocation}
          returnLocation={orderDetail?.returnLocation}
          pickupDate={orderDetail?.pickupDate}
          rentDays={orderDetail?.rentDays}
        />

        {/* 出行日期选择 */}
        <DateSelector
          selectedDate={orderDetail?.pickupDate || ""}
          endDate={routeState?.bookingFormData?.endDate}
          days={tourProduct?.days || orderDetail?.rentDays || 1}
          nights={tourProduct?.nights || (tourProduct?.days || 1) - 1}
          readOnly={!!orderId}
          onModify={isGroupFormat && !orderId ? undefined : () => setShowDatePicker(true)}
        />

        <ProductCard
          productInfo={productInfo!}
          productType="tour"
          basePrice={baseTotal}
          adultPrice={orderDetail?.adult_price}
          childPrice={orderDetail?.child_price}
          carAmount={orderDetail?.carAmount}
          carTitle={orderDetail?.carTitle}
          coverImage={orderDetail?.coverImage}
          pickupLocation={orderDetail?.pickupLocation}
          rentDays={orderDetail?.rentDays}
          insurance={insurance}
          onInsuranceChange={setInsurance as any}
          carExtraUpgrade={false}
          onCarExtraToggle={() => {}}
        />

        <ContactInfo
          contact={contact}
          defaultContact={orderDetail?.contact}
          onChange={setContact}
        />

        <TravelersList
          productType="tour"
          travelers={travelers}
          onTravelersChange={setTravelers}
        />

        <SpecialRequest
          defaultValue={orderDetail?.special_requests?.join("；")}
          onChange={setSpecialRequest}
        />

        {/* 价格明细（不在固定底部栏内） */}
        <div className="rounded-xl p-3 space-y-1.5" style={{ background: "#FFFDF8" }}>
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500">单价</span>
            <span className="font-medium text-gray-800">¥{adultPrice}/人</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500">×{effectiveAdultCount + childCount}人</span>
            <span className="font-medium text-gray-800">
              ¥{(adultPrice * (effectiveAdultCount + childCount)).toLocaleString()}
            </span>
          </div>
          <div
            className="flex items-center justify-between pt-1.5"
            style={{ borderTop: "1px dashed #EDE4D8" }}
          >
            <span className="text-sm font-bold" style={{ color: "#F97316" }}>行程总费用</span>
            <span className="text-lg font-bold" style={{ color: "#F97316" }}>
              ¥{total.toLocaleString()}
            </span>
          </div>
        </div>
      </div>

      <OrderFooter
        ready={!!orderDetail?.pickupDate}
        loading={loading}
        onSubmit={handleConfirmAndPay}
      />

      {/* 日期选择弹窗 */}
      <DatePickerModal
        open={showDatePicker}
        calendarList={tourProduct?.calendarList || []}
        selectedDate={orderDetail?.pickupDate || ""}
        days={tourProduct?.days || orderDetail?.rentDays || 1}
        nights={
          tourProduct?.nights ||
          (tourProduct?.days || orderDetail?.rentDays || 1) - 1
        }
        onClose={() => setShowDatePicker(false)}
        onConfirm={(date) => {
          setOrderDetail((prev) =>
            prev ? { ...prev, pickupDate: date } : null,
          );
          setShowDatePicker(false);
        }}
      />
    </div>
  );
}
